import React from "react";
import { cn } from "@/lib/utils";

/**
 * Hiển thị nội dung phản hồi/bình luận cho người dùng cuối.
 * Nội dung chuẩn là chữ thường, nhưng dữ liệu cũ (do agent viết) còn markdown: **đậm**, `mã`, "- " gạch đầu dòng,
 * "# " tiêu đề và khối <!-- TECHNICAL_AUDIT_TRAIL -->. Component này hiển thị gọn các định dạng đó thay vì in nguyên ký tự,
 * bỏ khối kỹ thuật, và không dùng HTML thô (an toàn XSS).
 */
const TECH_DELIMITER = "<!-- TECHNICAL_AUDIT_TRAIL -->";

/** Bỏ phần kỹ thuật (khối audit trail hoặc từ tiêu đề "### " đầu tiên) */
export function userFacingPart(text: string | null | undefined): string {
  if (!text) return "";
  let out = text.includes(TECH_DELIMITER) ? text.split(TECH_DELIMITER)[0] : text;
  const heading = out.search(/(^|\n)#{2,}\s/);
  if (heading > 0) out = out.slice(0, heading);
  return out.trim();
}

/** Chữ thuần (cho xem trước 1–2 dòng, thông báo...) */
export function stripFeedbackMarkdown(text: string | null | undefined): string {
  return userFacingPart(text)
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/__(.+?)__/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/^\s*#{1,6}\s+/gm, "")
    .replace(/^\s*[-*•]\s+/gm, "• ")
    .replace(/[ \t]+\n/g, "\n")
    .trim();
}

function renderInline(line: string, keyPrefix: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  const pattern = /\*\*(.+?)\*\*|__(.+?)__|`([^`]+)`/g;
  let last = 0;
  let match: RegExpExecArray | null;
  let i = 0;
  while ((match = pattern.exec(line))) {
    if (match.index > last) nodes.push(line.slice(last, match.index));
    if (match[1] !== undefined || match[2] !== undefined) {
      nodes.push(
        <strong key={`${keyPrefix}-b${i++}`} className="font-semibold text-slate-900">
          {/* agent text often nests code inside bold: **Loại SP (`/product-types`)** */}
          {(match[1] ?? match[2]).replace(/`([^`]+)`/g, "$1")}
        </strong>,
      );
    } else {
      nodes.push(
        <span key={`${keyPrefix}-c${i++}`} className="font-medium text-slate-900">
          {match[3]}
        </span>,
      );
    }
    last = match.index + match[0].length;
  }
  if (last < line.length) nodes.push(line.slice(last));
  return nodes;
}

interface FeedbackRichTextProps {
  text: string | null | undefined;
  fallback?: string;
  className?: string;
  "data-testid"?: string;
}

export function FeedbackRichText({ text, fallback = "", className, ...rest }: FeedbackRichTextProps) {
  const content = userFacingPart(text) || fallback;
  const lines = content.split("\n");

  return (
    <div data-testid={rest["data-testid"]} className={cn("text-sm break-words leading-relaxed space-y-1", className)}>
      {lines.map((raw, idx) => {
        const line = raw.trim();
        if (!line) return <div key={idx} className="h-1" aria-hidden />;
        const heading = line.match(/^#{1,6}\s+(.*)$/);
        if (heading) {
          return (
            <p key={idx} className="font-semibold text-slate-900">
              {renderInline(heading[1], `h${idx}`)}
            </p>
          );
        }
        const bullet = line.match(/^[-*•]\s+(.*)$/);
        if (bullet) {
          return (
            <div key={idx} className="flex items-start gap-2 pl-1">
              <span className="text-slate-400 shrink-0" aria-hidden>
                •
              </span>
              <span className="min-w-0">{renderInline(bullet[1], `l${idx}`)}</span>
            </div>
          );
        }
        return <p key={idx}>{renderInline(line, `p${idx}`)}</p>;
      })}
    </div>
  );
}
